import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QuotePageApi,
  QuotePageApiError,
  destinationLabel,
  sampleAmountTiersFor,
  type CrossBorderRoute,
  type EstimateMode,
  type EstimateResponse,
  type PayinCountry,
} from '@/lib/api';

// Reads the signed-link token from the URL path — the page is served at
// {pageBaseUrl}/{token} (see CrossBorderQuoteCollectExecutor's formUrl construction).
// No router needed for a single-page app; the last path segment is always the token.
function readTokenFromPath(): string {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? '';
}

const DEBOUNCE_MS = 400;

// Unique key for a destination option — see destinationKey state comment for why country
// alone isn't sufficient (a country can have multiple distinctly-priced payout variants).
function destinationOptionKey(r: CrossBorderRoute): string {
  return `${r.destinationCountry ?? ''}|${r.destinationCurrency}|${r.payoutMethodVariant ?? ''}`;
}

// Label shown in the destination dropdown. Only appends a currency/variant suffix when the
// country actually has more than one option — every single-variant country (the common case)
// keeps exactly the plain "flag + name" label this page has always shown.
function destinationOptionLabel(r: CrossBorderRoute, countryOptionCounts: Map<string, number>): string {
  const base = destinationLabel(r.destinationCountry as string, r.destinationCurrency);
  const country = r.destinationCountry as string;
  if ((countryOptionCounts.get(country) ?? 0) <= 1) return base;
  return `${base} — ${r.destinationCurrency}`;
}

export default function App() {
  const { t } = useTranslation();
  const token = useMemo(readTokenFromPath, []);
  const api = useMemo(() => new QuotePageApi(token), [token]);

  const [countries, setCountries] = useState<PayinCountry[]>([]);
  const [routes, setRoutes] = useState<CrossBorderRoute[]>([]);
  // providerId -> payin countries that provider accepts (e.g. bridge: ['US']) — used to scope
  // the destination dropdown to only the provider(s) serving the selected payin country. See
  // fetchPayinCountries' own comment on why route-level sourceCurrency can't do this (every
  // full_remittance_rail route, Vita's included, declares sourceCurrency: 'USD').
  const [payinByProvider, setPayinByProvider] = useState<Record<string, string[]>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [sourceCountry, setSourceCountry] = useState('');
  // Selection key is a composite of country|currency|variant, not destinationCountry alone.
  // Two distinct reasons a plain country key doesn't work:
  //   1. Multiple distinct countries share the same USD-currency corridor (Panama, USA,
  //      Canada, Hong Kong, China, international SWIFT) — currency alone can't identify a
  //      unique destination either (confirmed live 2026-07-30, Panama missing from the list).
  //   2. A single country can have multiple provider-priced payout variants for DIFFERENT
  //      currencies — e.g. Europe offers plain EUR/SEPA and a separate USD-denominated
  //      payout (payoutMethodVariant 'vita_euusd'), each a real, distinctly-priced option.
  //      Confirmed live 2026-08-01: a customer picked EUR here, then picked the USD option
  //      in a LATER WhatsApp menu by mistake — the page never offered the choice up front,
  //      so the two moments of "pick a currency" weren't the same moment, and the customer's
  //      first (real, confirmed) choice was silently overridden by the second.
  // The key is `${destinationCountry}|${destinationCurrency}|${payoutMethodVariant ?? ''}`.
  const [destinationKey, setDestinationKey] = useState('');
  // PR-CROSSBORDER-QUOTE-PAGE-UX-V1: no explicit mode toggle — whichever box the customer
  // types into determines the API mode for that request (matching a competitor's UX,
  // confirmed by direct comparison: our old two-button toggle read as an extra decision the
  // customer had to make before typing anything). `activeMode` tracks which box was most
  // recently edited; the OTHER box is always the read-only, auto-computed one.
  const [activeMode, setActiveMode] = useState<EstimateMode>('sender_pays');
  const [sendAmountInput, setSendAmountInput] = useState('');
  const [receiveAmountInput, setReceiveAmountInput] = useState('');

  const [quote, setQuote] = useState<EstimateResponse | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against out-of-order responses: rapid input changes (e.g. typing
  // an amount digit by digit) can fire several debounced fetches whose
  // responses arrive out of order over a real network. Without this, a
  // slower EARLIER request resolving after a faster LATER one overwrites
  // fresh state (or a fresh error) with stale data — confirmed live
  // 2026-07-23: a stale successful fee ("Comisión: 1.99 USD") rendered
  // alongside the CURRENT amount's genuine failure message, because the
  // failing request's setQuote(null) had already run before the stale
  // request's setQuote(result) landed afterward.
  const quoteRequestSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [countriesRes, routesRes] = await Promise.all([
          api.fetchPayinCountries(),
          api.fetchRoutes(),
        ]);
        if (cancelled) return;
        setCountries(countriesRes.countries);
        setPayinByProvider(countriesRes.byProvider);
        setRoutes(routesRes.routes);
        if (countriesRes.countries.length > 0) {
          setSourceCountry(countriesRes.countries[0].country);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof QuotePageApiError && err.status === 404) {
          setLoadError(t('errors.tokenExpired'));
        } else {
          setLoadError(t('errors.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sourceCurrency = useMemo(
    () => countries.find((c) => c.country === sourceCountry)?.currency ?? '',
    [countries, sourceCountry],
  );

  // Which provider(s) accept the selected payin country — the only real signal for scoping
  // destinations, since route-level sourceCurrency is always 'USD' regardless of provider.
  const allowedProviderIds = useMemo(
    () => new Set(
      Object.entries(payinByProvider)
        .filter(([, countries]) => countries.includes(sourceCountry))
        .map(([providerId]) => providerId),
    ),
    [payinByProvider, sourceCountry],
  );

  const destinationOptions = useMemo(() => {
    const seen = new Set<string>();
    return routes
      .filter((r) => r.railType === 'full_remittance_rail' && r.destinationCountry != null)
      .filter((r) => allowedProviderIds.size === 0 || allowedProviderIds.has(r.providerId))
      .filter((r) => {
        // Dedup by country+currency+variant — see destinationKey state comment above.
        // Country alone would silently collapse e.g. EU/EUR and EU/USD(vita_euusd) into
        // a single option, hiding a real, distinctly-priced choice from the customer.
        const key = destinationOptionKey(r);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [routes, allowedProviderIds]);

  // Selected destination may no longer be valid once the payin country changes (a different
  // provider's destination set) — reset so the effect below picks a fresh default.
  useEffect(() => {
    setDestinationKey('');
  }, [sourceCountry]);

  useEffect(() => {
    if (destinationOptions.length > 0 && !destinationKey) {
      setDestinationKey(destinationOptionKey(destinationOptions[0]));
    }
  }, [destinationOptions, destinationKey]);

  // Both dropdowns sorted A-Z by their display label (not insertion/API order) — confirmed
  // the raw lists were never alphabetically ordered, just whatever order the backend/mapping
  // table happened to return.
  const sortedCountries = useMemo(
    () => [...countries].sort((a, b) => (a.label ?? a.country).localeCompare(b.label ?? b.country, 'es')),
    [countries],
  );

  // Count how many options share a country, so the variant suffix ("— USD") only shows
  // when disambiguation is actually needed — a country with just one option (the common
  // case) keeps its plain, uncluttered label exactly as before this change.
  const countryOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of destinationOptions) {
      const country = r.destinationCountry as string;
      counts.set(country, (counts.get(country) ?? 0) + 1);
    }
    return counts;
  }, [destinationOptions]);

  const sortedDestinationOptions = useMemo(
    () => [...destinationOptions].sort((a, b) =>
      destinationOptionLabel(a, countryOptionCounts)
        .localeCompare(destinationOptionLabel(b, countryOptionCounts), 'es')
    ),
    [destinationOptions, countryOptionCounts],
  );

  const selectedDestination = useMemo(
    () => destinationOptions.find((r) => destinationOptionKey(r) === destinationKey) ?? null,
    [destinationOptions, destinationKey],
  );

  const destinationCountry = selectedDestination?.destinationCountry ?? '';
  const destinationCurrency = selectedDestination?.destinationCurrency ?? '';
  const payoutMethodVariant = selectedDestination?.payoutMethodVariant ?? null;

  // Live-updating quote: debounced re-fetch on any input change, matching a competitor's
  // "type in either box" calculator behavior — a real /estimate call, not client-side math
  // (the whole reason this is a bespoke page and not the generic forms renderer, which can
  // only do local math on values already in the form). Fetches using whichever box the
  // customer most recently typed into (activeMode) and writes the result into the OTHER box
  // only — never overwrites the box the customer is actively editing, so a fresh keystroke
  // is never clobbered by a slower in-flight response for the old value.
  const activeAmountInput = activeMode === 'sender_pays' ? sendAmountInput : receiveAmountInput;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const amountNum = parseFloat(activeAmountInput);
    if (!sourceCountry || !sourceCurrency || !destinationCountry || !destinationCurrency || !(amountNum > 0)) {
      quoteRequestSeq.current++; // invalidate any in-flight request so it can't resurrect a quote below
      setQuote(null);
      if (activeMode === 'sender_pays') setReceiveAmountInput('');
      else setSendAmountInput('');
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const requestId = ++quoteRequestSeq.current;
      setQuoting(true);
      setQuoteError(null);
      try {
        const result = await api.fetchEstimate({
          sourceCountry,
          sourceCurrency,
          destinationCountry,
          destinationCurrency,
          payoutMethodVariant,
          mode: activeMode,
          amount: activeAmountInput,
        });
        if (requestId !== quoteRequestSeq.current) return; // superseded by a newer request
        setQuote(result);
        // Write the computed side into the OTHER box only — the box the customer typed into
        // keeps showing exactly what they typed, verbatim.
        if (activeMode === 'sender_pays') setReceiveAmountInput(result.recipientReceives.amount);
        else setSendAmountInput(result.senderPays.amount);
      } catch (err) {
        if (requestId !== quoteRequestSeq.current) return; // superseded by a newer request
        setQuote(null);
        if (activeMode === 'sender_pays') setReceiveAmountInput('');
        else setSendAmountInput('');
        // eslint-disable-next-line no-console
        console.error('[quote-page] estimate failed', err);
        if (err instanceof QuotePageApiError && err.code === 'AMOUNT_TOO_SMALL') {
          setQuoteError(t('errors.amountTooSmall'));
        } else {
          setQuoteError(t('errors.quoteFailed'));
        }
      } finally {
        if (requestId === quoteRequestSeq.current) setQuoting(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceCountry, sourceCurrency, destinationCountry, destinationCurrency, payoutMethodVariant, activeMode, activeAmountInput, api, t]);

  // PR-CROSSBORDER-QUOTE-PAGE-RATE-PREVIEW-V1: "send more, get a better rate" preview —
  // three live quotes at round, currency-native sample amounts, fetched as soon as
  // source+destination are both picked and BEFORE the customer types any amount of their
  // own. Fixed transaction costs matter proportionally less at higher amounts, so a
  // higher tier's effective rate is always at least as good as a lower one's — this makes
  // that concretely visible instead of asking the customer to intuit it. Deliberately a
  // SEPARATE effect/request sequence from the main live-quote effect above (different
  // trigger — currency/destination changes, not amount typing — and different guard
  // counter) so an in-flight amount-typing debounce and this tier fetch never race or
  // clobber each other's state.
  const [sampleTiers, setSampleTiers] = useState<Array<{ amount: number; result: EstimateResponse } | null>>([]);
  const [sampleTiersLoading, setSampleTiersLoading] = useState(false);
  const sampleTiersSeq = useRef(0);

  useEffect(() => {
    if (!sourceCountry || !sourceCurrency || !destinationCountry || !destinationCurrency) {
      sampleTiersSeq.current++;
      setSampleTiers([]);
      return;
    }

    const requestId = ++sampleTiersSeq.current;
    const tiers = sampleAmountTiersFor(sourceCurrency);
    setSampleTiersLoading(true);

    (async () => {
      const results = await Promise.all(
        tiers.map(async (amount) => {
          try {
            const result = await api.fetchEstimate({
              sourceCountry,
              sourceCurrency,
              destinationCountry,
              destinationCurrency,
              payoutMethodVariant,
              mode: 'sender_pays',
              amount: String(amount),
            });
            return { amount, result };
          } catch {
            // A tier below the corridor's real minimum (e.g. the 10-USD-equivalent tier
            // for a corridor whose minimum is higher) legitimately fails — omit that row
            // rather than showing an error for what's meant to be an illustrative preview.
            return null;
          }
        }),
      );
      if (requestId !== sampleTiersSeq.current) return; // superseded by a newer request
      setSampleTiers(results);
      setSampleTiersLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceCountry, sourceCurrency, destinationCountry, destinationCurrency, payoutMethodVariant, api]);

  const handleSubmit = async () => {
    if (!quote) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.submit({
        sourceCountry,
        sourceCurrency,
        destinationCountry,
        destinationCurrency,
        payoutMethodVariant,
        mode: activeMode,
        amount: activeAmountInput,
      });
      setSubmitted(true);
    } catch {
      setSubmitError(t('errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-gray-600">
        {t('loading')}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-center text-red-600">{loadError}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900">{t('submitted.title')}</h1>
          <p className="mt-2 text-gray-600">{t('submitted.body')}</p>
          <p className="mt-2 text-gray-600">{t('submitted.closeHint')}</p>
        </div>
      </div>
    );
  }

  // quote.rate is destination-per-SETTLEMENT-currency (USD) — not meaningful to a
  // customer who never sees USD. The real customer-facing tasa is computed here from
  // the two final real amounts already in the response, same convention as the flow's
  // build_summary_v2 (perUnit = receive / send). Displayed inverted (1 destination = X
  // source) per product request — a "how many pesos per bolívar" factor reads more
  // naturally than a sub-1 decimal for currency pairs where source > destination in value.
  const effectiveRate = quote && parseFloat(quote.recipientReceives.amount) > 0
    ? parseFloat(quote.senderPays.amount) / parseFloat(quote.recipientReceives.amount)
    : null;

  // Regression: BRL->COP (and any other pair where 1 destination-currency unit is
  // worth a small fraction of the source currency, e.g. 1 COP = 0.0016 BRL) rendered
  // as "1 COP = 0.00 BRL" — toFixed(2) truncates anything below 0.005 to zero. Mirrors
  // the same magnitude-aware scaling build_summary_v2 already uses in the WhatsApp flow
  // (corasend-remittance.yaml): scale the DESTINATION unit up (1 -> 1,000) until the
  // ratio reads as a human-readable number, instead of fixing decimal places on a
  // possibly sub-0.01 raw ratio. Returns the whole "N destinationCurrency = X
  // sourceCurrency" line, not just the numeric fragment, since the unit itself changes.
  const formatRateLine = (value: number): string => {
    if (value >= 0.01) {
      return `1 ${destinationCurrency} = ${value.toFixed(2)} ${sourceCurrency}`;
    }
    const unit = value * 1000 >= 0.01 ? 1000 : 100000;
    return `${unit.toLocaleString('es-CO')} ${destinationCurrency} = ${(value * unit).toFixed(2)} ${sourceCurrency}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="mx-auto max-w-md rounded-xl bg-white p-5 shadow-sm">
        <h1 className="text-lg font-semibold text-gray-900">{t('title')}</h1>

        <label className="mt-4 block text-xs font-medium uppercase text-gray-500">
          {t('source.label')}
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-base"
            value={sourceCountry}
            onChange={(e) => setSourceCountry(e.target.value)}
          >
            {sortedCountries.map((c) => (
              <option key={c.country} value={c.country}>
                {c.label ?? c.country} ({c.currency})
              </option>
            ))}
          </select>
        </label>

        <label className="mt-4 block text-xs font-medium uppercase text-gray-500">
          {t('destination.label')}
          <select
            className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-base"
            value={destinationKey}
            onChange={(e) => setDestinationKey(e.target.value)}
          >
            {sortedDestinationOptions.map((r) => (
              <option key={destinationOptionKey(r)} value={destinationOptionKey(r)}>
                {destinationOptionLabel(r, countryOptionCounts)} ({r.destinationCurrency})
              </option>
            ))}
          </select>
        </label>

        {/* PR-CROSSBORDER-QUOTE-PAGE-RATE-PREVIEW-V1: shown before the customer types any
            amount — three real quotes at round sample amounts, so a higher effective rate
            at higher volumes is visible up front rather than something the customer has to
            discover by trial and error after committing to an amount. */}
        {sampleTiersLoading && sampleTiers.length === 0 && (
          <p className="mt-3 text-xs text-gray-400">{t('loading')}</p>
        )}
        {sampleTiers.some((tier) => tier != null) && (
          <div className="mt-3 rounded-lg bg-gray-50 p-3">
            <p className="text-xs font-medium uppercase text-gray-500">{t('samplePreview.label')}</p>
            <table className="mt-2 w-full text-sm text-gray-700">
              <tbody>
                {sampleTiers.map((tier, idx) => {
                  if (!tier) return null;
                  const rate = parseFloat(tier.result.recipientReceives.amount) / tier.amount;
                  return (
                    <tr key={idx}>
                      <td className="py-0.5 pr-2 text-right tabular-nums">
                        {tier.amount.toLocaleString('es-CO')} {sourceCurrency}
                      </td>
                      <td className="py-0.5 px-1 text-gray-400">=</td>
                      <td className="py-0.5 pl-1 text-left tabular-nums">
                        {parseFloat(tier.result.recipientReceives.amount).toLocaleString('es-CO')} {destinationCurrency}
                        <span className="ml-1 text-xs text-gray-400">
                          (1 {sourceCurrency} = {rate.toFixed(4)} {destinationCurrency})
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PR-CROSSBORDER-QUOTE-PAGE-UX-V1: no mode-toggle buttons — type into either box
            and the other one auto-computes, matching a competitor's calculator UX. Both
            boxes are always editable; whichever one the customer typed into last becomes
            activeMode/activeAmountInput (see the quote-fetching effect above), and its
            counterpart is overwritten with the live-computed result once the debounced
            estimate resolves. */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium uppercase text-gray-500">
            {t('amount.youSend')}
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-base"
              inputMode="decimal"
              placeholder={sourceCurrency}
              value={sendAmountInput}
              onChange={(e) => {
                setActiveMode('sender_pays');
                setSendAmountInput(e.target.value.replace(/[^0-9.]/g, ''));
              }}
            />
          </label>
          <label className="block text-xs font-medium uppercase text-gray-500">
            {t('amount.theyReceive')}
            <input
              className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-base"
              inputMode="decimal"
              placeholder={destinationCurrency}
              value={receiveAmountInput}
              onChange={(e) => {
                setActiveMode('recipient_receives');
                setReceiveAmountInput(e.target.value.replace(/[^0-9.]/g, ''));
              }}
            />
          </label>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          {quoting && <p>{t('loading')}</p>}
          {!quoting && !quoteError && quote && effectiveRate != null && (
            <p>{t('rate.label')} {formatRateLine(effectiveRate)}</p>
          )}
          {!quoting && quoteError && (
            <>
              <p className="text-red-600">{quoteError}</p>
              <p className="mt-1 text-gray-500">{t('errors.quoteFailedHint')}</p>
            </>
          )}
        </div>

        {submitError && <p className="mt-2 text-sm text-red-600">{submitError}</p>}

        <button
          type="button"
          className="mt-4 w-full rounded-lg bg-brand-600 p-3 text-base font-semibold text-white disabled:opacity-50"
          disabled={!quote || submitting}
          onClick={handleSubmit}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      </div>
    </div>
  );
}
